import { useCallback, useEffect, useState } from "react";
import { fetchFoodRetailSourceSelection } from "../../../../api/foodApi";

function toneClass(tone) {
  return `food-retail-selection__banner food-retail-selection__banner--${tone || "neutral"}`;
}

const FoodRetailSourceSelectionPanel = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadSelection = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchFoodRetailSourceSelection();
      setPayload(data);
    } catch (error) {
      setPayload(null);
      setErrorMessage(error.message || "Unable to load retail source selection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSelection();
  }, [loadSelection]);

  const selected = payload?.selectedCandidate;
  const hasSelection = Boolean(selected?.selectedSourceKey);
  const blockedCandidates = (payload?.candidates || []).filter(
    (candidate) => !candidate.isEligible
  );

  return (
    <section className="panel-card food-retail-selection">
      <header className="food-retail-selection__header">
        <div>
          <h2 className="admin-dash__card-title">Retail Source Selection</h2>
          <p className="muted">
            Read-only decision from supermarket discovery metadata — no ingestion runs here.
          </p>
        </div>
        <button
          type="button"
          className="admin-dashboard__action-btn"
          onClick={loadSelection}
          disabled={loading}
        >
          Refresh
        </button>
      </header>

      {loading ? <p className="muted">Evaluating retail supermarket candidates…</p> : null}
      {errorMessage ? <p className="food-discovery-review__error">{errorMessage}</p> : null}

      {!loading && !errorMessage && payload ? (
        <>
          <div className={toneClass(hasSelection ? "success" : "warning")}>
            {hasSelection ? (
              <>
                <strong>
                  {selected.selectedSourceName} ({selected.selectedSourceKey})
                </strong>{" "}
                is ready for the retail ingestion PoC.
                <div className="muted">{selected.selectionReason}</div>
                <div className="food-retail-selection__next">
                  <strong>Next engineering step:</strong> {selected.nextEngineeringStep}
                </div>
              </>
            ) : (
              <>
                <strong>No retail supermarket source is ready for ingestion.</strong>
                <div className="muted">{payload.noEligibleSourceMessage}</div>
                <div className="food-retail-selection__next">
                  Complete discovery review for Éxito (EXITO_SUPERMARKET, priority 1) or
                  another chain before building a provider.
                </div>
              </>
            )}
          </div>

          {hasSelection ? (
            <div className="food-retail-selection__details">
              <dl>
                <div>
                  <dt>Recommended access</dt>
                  <dd>{selected.recommendedAccessMethod}</dd>
                </div>
                <div>
                  <dt>Terms review</dt>
                  <dd>{selected.termsReviewStatus}</dd>
                </div>
                <div>
                  <dt>Ingestion risk</dt>
                  <dd>{selected.riskLevel}</dd>
                </div>
                <div>
                  <dt>Sample category</dt>
                  <dd>
                    {selected.sampleCategoryUrl ? (
                      <a href={selected.sampleCategoryUrl} target="_blank" rel="noreferrer">
                        {selected.sampleCategoryUrl}
                      </a>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Sample product</dt>
                  <dd>
                    {selected.sampleProductUrl ? (
                      <a href={selected.sampleProductUrl} target="_blank" rel="noreferrer">
                        {selected.sampleProductUrl}
                      </a>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              </dl>

              {selected.warnings?.length ? (
                <div className="food-retail-selection__warnings">
                  <div className="eyebrow">Warnings</div>
                  <ul>
                    {selected.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {payload.ingestionStrategy ? (
                <div className="food-retail-selection__strategy">
                  <div className="eyebrow">Recommended build targets</div>
                  <ul>
                    <li>{payload.ingestionStrategy.providerFileRecommendation}</li>
                    <li>{payload.ingestionStrategy.runnerRecommendation}</li>
                    <li>{payload.ingestionStrategy.adminPageRecommendation}</li>
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}

          {blockedCandidates.length ? (
            <div className="food-retail-selection__blocked">
              <div className="eyebrow">Blocking reasons by source</div>
              <div className="food-retail-selection__blocked-grid">
                {blockedCandidates.map((candidate) => (
                  <div key={candidate.sourceKey} className="food-retail-selection__blocked-card">
                    <strong>{candidate.sourceName}</strong>
                    <span className="admin-data-health-code">{candidate.sourceKey}</span>
                    <ul>
                      {candidate.blockingReasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                    {candidate.warnings?.length ? (
                      <>
                        <div className="eyebrow">Warnings</div>
                        <ul>
                          {candidate.warnings.map((warning) => (
                            <li key={warning}>{warning}</li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default FoodRetailSourceSelectionPanel;
